import { useState } from "react";
import { ActionButton } from "./ActionButton";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { useToast } from "../Toast";
import { useAdminActions } from "../../hooks/useAdminActions";
import type { UserDetail } from "../../hooks/useAdminUsers";

interface ActionPanelProps {
  user: UserDetail;
  onActionComplete?: () => void;
}

type ActionType =
  | "reset-password"
  | "resend-verification"
  | "verify-email"
  | "apply-credit"
  | "cancel-subscription"
  | "delete-account"
  | null;

/**
 * ActionPanel - Admin action buttons for user management.
 *
 * Displays contextual action buttons based on user state:
 * - Reset Password: Always available
 * - Resend Verification: Only if email not verified
 * - Verify Email Manually: Only if email not verified
 * - Apply Credit: Only if user has Stripe customer
 * - Cancel Subscription: Only if user has active paid subscription
 * - Delete Account: Always available (danger zone)
 */
export function ActionPanel({ user, onActionComplete }: ActionPanelProps) {
  const { addToast } = useToast();
  const actions = useAdminActions();
  const [activeAction, setActiveAction] = useState<ActionType>(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [hardDelete, setHardDelete] = useState(false);

  const profileId = user.profile.id;
  const isEmailVerified = user.profile.emailVerified;
  const hasStripeCustomer = !!user.subscription?.stripeCustomerId;
  const hasPaidSubscription =
    user.subscription?.tier !== "free" &&
    user.subscription?.status === "active";

  // Handle action confirmation
  const handleConfirm = async (reason?: string) => {
    let result: { success: boolean; message: string };

    switch (activeAction) {
      case "reset-password":
        result = await actions.resetPassword.execute(profileId);
        break;
      case "resend-verification":
        result = await actions.resendVerification.execute(profileId);
        break;
      case "verify-email":
        result = await actions.verifyEmail.execute(profileId);
        break;
      case "apply-credit":
        const amountCents = Math.round(parseFloat(creditAmount) * 100);
        if (isNaN(amountCents) || amountCents <= 0) {
          addToast({ type: "error", message: "Please enter a valid amount" });
          return;
        }
        result = await actions.applyCredit.execute(profileId, {
          amountCents,
          reason: reason || "Admin credit",
        });
        break;
      case "cancel-subscription":
        result = await actions.cancelSubscription.execute(profileId, {
          reason: reason || "Admin canceled",
          immediately: cancelImmediately,
        });
        break;
      case "delete-account":
        result = await actions.deleteAccount.execute(profileId, {
          reason: reason || "Admin deleted",
          hardDelete,
        });
        break;
      default:
        return;
    }

    if (result.success) {
      addToast({ type: "success", message: result.message });
      onActionComplete?.();
    } else {
      addToast({ type: "error", message: result.message });
    }

    setActiveAction(null);
    setCreditAmount("");
    setCancelImmediately(false);
    setHardDelete(false);
  };

  // Check if any action is loading
  const isLoading =
    actions.resetPassword.isLoading ||
    actions.resendVerification.isLoading ||
    actions.verifyEmail.isLoading ||
    actions.applyCredit.isLoading ||
    actions.cancelSubscription.isLoading ||
    actions.deleteAccount.isLoading;

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Actions</h2>
        </div>

        {/* Action Buttons */}
        <div className="p-6">
          {/* Primary Actions Row */}
          <div className="flex flex-wrap gap-3 mb-6">
            <ActionButton
              label="Reset Password"
              icon={<LockIcon />}
              variant="secondary"
              onClick={() => setActiveAction("reset-password")}
              disabled={isLoading}
            />

            {!isEmailVerified && (
              <>
                <ActionButton
                  label="Resend Verification"
                  icon={<MailIcon />}
                  variant="secondary"
                  onClick={() => setActiveAction("resend-verification")}
                  disabled={isLoading}
                />
                <ActionButton
                  label="Verify Email"
                  icon={<CheckIcon />}
                  variant="primary"
                  onClick={() => setActiveAction("verify-email")}
                  disabled={isLoading}
                />
              </>
            )}
          </div>

          {/* Subscription Actions Row */}
          <div className="flex flex-wrap gap-3 mb-6">
            {hasStripeCustomer && (
              <ActionButton
                label="Apply Credit"
                icon={<CreditCardIcon />}
                variant="secondary"
                onClick={() => setActiveAction("apply-credit")}
                disabled={isLoading}
              />
            )}

            {hasPaidSubscription && (
              <ActionButton
                label="Cancel Subscription"
                icon={<XCircleIcon />}
                variant="secondary"
                onClick={() => setActiveAction("cancel-subscription")}
                disabled={isLoading}
              />
            )}
          </div>

          {/* Danger Zone */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              Danger Zone
            </h3>
            <ActionButton
              label="Delete Account"
              icon={<TrashIcon />}
              variant="danger"
              onClick={() => setActiveAction("delete-account")}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        isOpen={activeAction === "reset-password"}
        title="Reset Password"
        description={`Send a password reset email to ${user.profile.email}?`}
        confirmLabel="Send Reset Email"
        variant="default"
        onConfirm={handleConfirm}
        onCancel={() => setActiveAction(null)}
        loading={actions.resetPassword.isLoading}
      />

      <ConfirmationDialog
        isOpen={activeAction === "resend-verification"}
        title="Resend Verification Email"
        description={`Send a verification email to ${user.profile.email}?`}
        confirmLabel="Send Verification"
        variant="default"
        onConfirm={handleConfirm}
        onCancel={() => setActiveAction(null)}
        loading={actions.resendVerification.isLoading}
      />

      <ConfirmationDialog
        isOpen={activeAction === "verify-email"}
        title="Verify Email Manually"
        description={`Mark ${user.profile.email} as verified? Only use this if the user is having email deliverability issues.`}
        confirmLabel="Verify Email"
        variant="default"
        onConfirm={handleConfirm}
        onCancel={() => setActiveAction(null)}
        loading={actions.verifyEmail.isLoading}
      />

      <ConfirmationDialog
        isOpen={activeAction === "apply-credit"}
        title="Apply Account Credit"
        description="Add credit to this user's Stripe account balance. This will be applied to their next invoice."
        confirmLabel="Apply Credit"
        variant="default"
        onConfirm={handleConfirm}
        onCancel={() => setActiveAction(null)}
        requireReason
        reasonLabel="Reason for credit"
        reasonPlaceholder="e.g., Refund for billing issue, Goodwill gesture..."
        loading={actions.applyCredit.isLoading}
      >
        <div className="mb-4">
          <label
            htmlFor="credit-amount"
            className="block text-sm font-medium text-gray-700 mb-1.5"
          >
            Amount (USD)
            <span className="text-red-500 ml-0.5">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
              $
            </span>
            <input
              type="number"
              id="credit-amount"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              placeholder="0.00"
              min="0.01"
              max="1000"
              step="0.01"
              className="
                w-full pl-8 pr-3 py-2
                text-sm text-gray-900
                border border-gray-300 rounded-lg
                focus:outline-none focus:ring-2 focus:ring-magic-500 focus:border-transparent
              "
            />
          </div>
        </div>
      </ConfirmationDialog>

      <ConfirmationDialog
        isOpen={activeAction === "cancel-subscription"}
        title="Cancel Subscription"
        description={`Cancel ${user.profile.name || user.profile.email}'s ${user.subscription?.tier} subscription?`}
        confirmLabel="Cancel Subscription"
        variant="danger"
        onConfirm={handleConfirm}
        onCancel={() => setActiveAction(null)}
        requireReason
        reasonLabel="Cancellation reason"
        reasonPlaceholder="e.g., User requested, Billing issue..."
        loading={actions.cancelSubscription.isLoading}
      >
        <div className="mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={cancelImmediately}
              onChange={(e) => setCancelImmediately(e.target.checked)}
              className="
                w-4 h-4 rounded
                text-magic-600
                border-gray-300
                focus:ring-magic-500
              "
            />
            <span className="text-sm text-gray-700">
              Cancel immediately (instead of at period end)
            </span>
          </label>
          <p className="mt-1 ml-6 text-xs text-gray-500">
            If unchecked, subscription will remain active until{" "}
            {user.subscription?.currentPeriodEnd
              ? new Date(user.subscription.currentPeriodEnd).toLocaleDateString()
              : "the end of the billing period"}
            .
          </p>
        </div>
      </ConfirmationDialog>

      <ConfirmationDialog
        isOpen={activeAction === "delete-account"}
        title="Delete User Account"
        description={`This will permanently affect ${user.profile.name || user.profile.email}'s account. This action cannot be easily undone.`}
        confirmLabel={hardDelete ? "Permanently Delete" : "Delete Account"}
        variant="danger"
        onConfirm={handleConfirm}
        onCancel={() => setActiveAction(null)}
        requireReason
        reasonLabel="Deletion reason"
        reasonPlaceholder="e.g., GDPR request, User requested, Spam account..."
        loading={actions.deleteAccount.isLoading}
      >
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={hardDelete}
              onChange={(e) => setHardDelete(e.target.checked)}
              className="
                w-4 h-4 rounded
                text-red-600
                border-gray-300
                focus:ring-red-500
              "
            />
            <span className="text-sm font-medium text-red-800">
              GDPR hard delete (permanent)
            </span>
          </label>
          <p className="mt-1 ml-6 text-xs text-red-700">
            {hardDelete
              ? "All user data will be permanently erased and cannot be recovered."
              : "User will be soft deleted. Data will be anonymized but retained."}
          </p>
        </div>
      </ConfirmationDialog>
    </>
  );
}

// ----- Icons -----

function LockIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CreditCardIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  );
}

function XCircleIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

export default ActionPanel;
